import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  Pressable,
  RefreshControl,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { schedulesApi, organizationsApi } from '../../services/apiService';
import { OrgScheduleSummary, ScheduleGlobalStats, OrganizationList } from '../../types';
import { formatPct } from '../../utils/formatPercentage';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useResponsive, useRoleNavigation } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { useYear } from '../../contexts/YearContext';
import { useOrgScheduleSummaryQuery } from '../../hooks/queries';
import { TableSkeleton } from '../../components/Skeleton';

import { tokens } from '@design';
import { IconButton } from '@design/components/IconButton';
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronRightIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EyeIcon = ({ size = 16, color = tokens.colors.raw.slate["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Circle cx="12" cy="12" r="3" stroke={color} strokeWidth="2"/>
  </Svg>
);

const PrinterIcon = ({ size = 18, color = tokens.colors.raw.slate["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M6 14h12v8H6v-8z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const FileOutputIcon = ({ size = 18, color = tokens.colors.raw.slate["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2v6h6M9 15h6M12 18l3-3-3-3" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Progress Ring Component (for completion percentage)
// ============================================================

interface ProgressRingProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  showLabel?: boolean;
}

const ProgressRing = ({ percentage, size = 80, strokeWidth = 6, showLabel = true }: ProgressRingProps) => {
  const { colors, isDark } = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  const getColor = () => {
    if (percentage >= 80) return tokens.colors.raw.green["500"];
    if (percentage >= 50) return tokens.colors.raw.yellow["500"];
    return tokens.colors.raw.red["500"];
  };

  const progressColor = getColor();
  const bgTrackColor = isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["200"];
  const dotStrokeColor = isDark ? colors.card : '#FCFCFC';

  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        <Circle cx={center} cy={center} r={radius} stroke={bgTrackColor} strokeWidth={strokeWidth} fill="none" />
        <Circle
          cx={center} cy={center} r={radius}
          stroke={progressColor} strokeWidth={strokeWidth} fill="none"
          strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
          strokeLinecap="round" transform={`rotate(-90 ${center} ${center})`}
        />
        <Circle cx={dotX} cy={dotY} r={5} fill={progressColor} stroke={dotStrokeColor} strokeWidth={2} />
      </Svg>
      {showLabel && (
        <View style={{ position: 'absolute', alignItems: 'center', justifyContent: 'center', width: size, height: size, paddingHorizontal: tokens.spacing.absolute["1"] }}>
          <Text
            numberOfLines={1}
            adjustsFontSizeToFit
            style={{ fontSize: Math.max(9, Math.round(size / 6)), fontFamily: FONTS.numbers, fontWeight: '600', color: colors.textPrimary }}
          >
            %{formatPct(percentage)}
          </Text>
        </View>
      )}
    </View>
  );
};

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
}

const Dropdown = ({ value, options, placeholder, onChange }: DropdownProps) => {
  const { colors, isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    buttonBg: colors.card,
    buttonBorder: colors.cardBorder,
    buttonText: colors.textPrimary,
    placeholderText: colors.textMuted,
    menuBg: colors.card,
    menuBorder: colors.cardBorder,
    itemBorder: isDark ? colors.border : tokens.colors.raw.slate["100"],
    itemText: colors.textPrimary,
  };

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        style={[styles.dropdownButton, { backgroundColor: dropdownTheme.buttonBg, borderColor: dropdownTheme.buttonBorder }]}
        onPress={() => setIsOpen(!isOpen)}
      >
        <Text style={[styles.dropdownButtonText, { color: selectedOption ? dropdownTheme.buttonText : dropdownTheme.placeholderText }]}>
          {selectedOption ? selectedOption.label : placeholder}
        </Text>
        <ChevronDownIcon size={16} color={colors.textSecondary} />
      </TouchableOpacity>

      {isOpen && (
        <Modal transparent visible={isOpen} onRequestClose={() => setIsOpen(false)}>
          <Pressable style={styles.modalOverlay} onPress={() => setIsOpen(false)}>
            <View style={[styles.dropdownMenu, { backgroundColor: dropdownTheme.menuBg, borderColor: dropdownTheme.menuBorder }]}>
              <ScrollView style={styles.dropdownMenuScroll}>
                {options.map((option) => (
                  <TouchableOpacity
                    key={option.value || '_all'}
                    style={[styles.dropdownItem, { borderBottomColor: dropdownTheme.itemBorder }]}
                    onPress={() => { onChange(option.value); setIsOpen(false); }}
                  >
                    <Text style={[styles.dropdownItemText, { color: dropdownTheme.itemText }]}>{option.label}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </Pressable>
        </Modal>
      )}
    </View>
  );
};

// ============================================================
// Main Screen Component
// ============================================================

export default function SchedulesManagementScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const { translateOrg } = useAutoTranslate();
  const { isMobile } = useResponsive();
  const { isViewOnly } = useRoleNavigation();
  const { selectedYear, setSelectedYear } = useYear();

  // State
  const [organizations, setOrganizations] = useState<OrganizationList[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  // Filter state (organization only — year comes from global YearContext)
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [pendingOrganization, setPendingOrganization] = useState<string>('');

  // Theme colors
  const themedColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    text: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    tableBorder: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["200"],
    tableHeaderBg: isDark ? tokens.colors.raw.gray["800"] : tokens.colors.raw.slate["50"],
    tableRowBg: colors.card,
  };

  // Fetch organizations (for dropdown)
  useEffect(() => {
    if (user?.profile?.user_type === 'strategy_team' || user?.profile?.user_type === 'superuser' || user?.profile?.user_type === 'executive') {
      fetchOrganizations();
    }
  }, [user]);

  const fetchOrganizations = async () => {
    try {
      const response = await organizationsApi.list({ page_size: 9999 });
      setOrganizations(response.results || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  // Cached server-side aggregated org summary. Re-visiting the page renders
  // the previous result from cache instantly (no spinner). Year change is
  // covered by YearContext's `['strategy']` invalidation.
  const orgSummaryQ = useOrgScheduleSummaryQuery({
    organization: selectedOrganization || null,
    page: currentPage,
    pageSize,
    year: selectedYear,
  });
  const orgSummaries = orgSummaryQ.data?.results ?? [];
  const globalStats = orgSummaryQ.data?.global_stats ?? {
    total_kpis: 0,
    total_readings: 0,
    due_and_completed: 0,
    overdue_count: 0,
  };
  const totalCount = orgSummaryQ.data?.count ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  const loading = orgSummaryQ.isPending;

  // Surface load failures (replaces the catch block in the old fetch).
  useEffect(() => {
    if (orgSummaryQ.error) {
      console.error('Error fetching org summary:', orgSummaryQ.error);
      showError(t('schedules.loadFailed'));
    }
  }, [orgSummaryQ.error, showError, t]);

  // Reset to page 1 when global year changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await orgSummaryQ.refetch();
    } finally {
      setRefreshing(false);
    }
  }, [orgSummaryQ]);

  // Handle apply filter (organization only — year is global)
  const handleApplyFilter = () => {
    setSelectedOrganization(pendingOrganization);
    setCurrentPage(1);
  };

  // Handle generate schedules (uses global year from YearContext)
  const handleGenerateSchedules = async () => {
    const orgToGenerate = pendingOrganization || selectedOrganization;
    if (!orgToGenerate) {
      showError(t('schedules.selectOrganizationFirst'));
      return;
    }

    try {
      setGenerating(true);
      await schedulesApi.generateByOrg(parseInt(orgToGenerate), selectedYear);
      showSuccess(t('schedules.generateSuccess'));
      // Auto-apply the org filter so the table shows the generated data
      setSelectedOrganization(orgToGenerate);
      setPendingOrganization(orgToGenerate);
      setCurrentPage(1);
      await orgSummaryQ.refetch();
    } catch (error: any) {
      console.error('Error generating schedules:', error);
      showError(t('schedules.generateError'));
    } finally {
      setGenerating(false);
    }
  };

  // Organization options for dropdown
  const organizationOptions: DropdownOption[] = [
    { value: '', label: t('schedules.allOrganizations') },
    ...organizations.map(org => ({
      value: org.id.toString(),
      label: translateOrg(org.name),
    })),
  ];

  // Print table
  const handlePrint = () => {
    if (Platform.OS === 'web') {
      window.print();
    }
  };

  // Export table as CSV
  const handleExportCSV = () => {
    if (Platform.OS !== 'web' || orgSummaries.length === 0) return;

    const headers = [
      t('schedules.organization'),
      t('schedules.kpiCount'),
      t('schedules.totalReadings'),
      t('schedules.dueCount'),
      t('schedules.overdueCount'),
      t('schedules.completionRate'),
    ];

    const rows = orgSummaries.map(org => [
      translateOrg(org.org_name),
      org.kpi_count,
      org.total_readings,
      org.due_count,
      org.overdue_count,
      `${Number(org.completion_percentage).toFixed(2)}%`,
    ]);

    // Add BOM for Arabic support in Excel
    const bom = '\uFEFF';
    const csv = bom + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `schedules_${selectedYear}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Pagination info text
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);
  const paginationText = t('schedules.showingItems', { start: startItem, end: endItem, total: totalCount });

  // Page numbers for pagination
  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // No more full-screen blocking spinner. The page frame renders instantly;
  // the table area below shows a skeleton on first cold load. Cached
  // revisits skip the skeleton entirely.

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: t('navigation.home') },
          { label: t('schedules.managementTitle') },
        ]}
      />

      <ScrollView
        style={styles.container}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {!isViewOnly && (
              <TouchableOpacity
                style={[styles.generateButton, generating && styles.generateButtonDisabled]}
                onPress={handleGenerateSchedules}
                disabled={generating}
              >
                {generating ? (
                  <ActivityIndicator size="small" color={COLORS.white} />
                ) : (
                  <PlusIcon size={16} color={COLORS.white} />
                )}
                <Text style={styles.generateButtonText}>{t('schedules.generateSchedule')}</Text>
              </TouchableOpacity>
            )}
          </View>
          <Text style={[styles.pageTitle, { color: themedColors.text }]}>{t('schedules.managementTitle')}</Text>
        </View>

        {/* Stats Cards */}
        <View style={[styles.statsContainer, isMobile && styles.statsContainerMobile]}>
          {/* Card 1: Total KPIs */}
          <View style={[styles.statCard, { backgroundColor: themedColors.card, borderColor: themedColors.cardBorder }]}>
            <Text style={[styles.statLabel, { color: themedColors.text }]}>{t('schedules.totalKPIs')}</Text>
            <Text style={[styles.statValue, { color: themedColors.text, fontFamily: FONTS.numbers }]}>
              {globalStats.total_kpis}
            </Text>
            <Text style={[styles.statSubtitle, { color: themedColors.text }]}>{t('schedules.kpiPerformance')}</Text>
          </View>

          {/* Card 2: Total Readings */}
          <View style={[styles.statCard, { backgroundColor: themedColors.card, borderColor: themedColors.cardBorder }]}>
            <Text style={[styles.statLabel, { color: themedColors.text }]}>{t('schedules.totalReadings')}</Text>
            <Text style={[styles.statValue, { color: themedColors.text, fontFamily: FONTS.numbers }]}>
              {globalStats.total_readings}
            </Text>
            <Text style={[styles.statSubtitle, { color: themedColors.text }]}>{t('schedules.dueAndCompletedReadings')}</Text>
          </View>

          {/* Card 3: Due Readings + view link */}
          <View style={[styles.statCard, { backgroundColor: themedColors.card, borderColor: themedColors.cardBorder }]}>
            <Text style={[styles.statLabel, { color: themedColors.text }]}>{t('schedules.dueAndCompleted')}</Text>
            <Text style={[styles.statValue, { color: themedColors.text, fontFamily: FONTS.numbers }]}>
              {globalStats.due_and_completed}
            </Text>
            <TouchableOpacity
              style={styles.statLink}
              onPress={() => navigation.navigate('Schedules', {})}
            >
              <Text style={[styles.statLinkText, { color: themedColors.text }]}>{t('schedules.viewReadings')}</Text>
              <EyeIcon size={16} color={themedColors.text} />
            </TouchableOpacity>
          </View>

          {/* Card 4: Overdue + view link */}
          <View style={[styles.statCard, { backgroundColor: themedColors.card, borderColor: themedColors.cardBorder }]}>
            <Text style={[styles.statLabel, { color: themedColors.text }]}>{t('schedules.overdueReadings')}</Text>
            <Text style={[styles.statValue, { color: themedColors.text, fontFamily: FONTS.numbers }]}>
              {globalStats.overdue_count}
            </Text>
            <TouchableOpacity
              style={styles.statLink}
              onPress={() => navigation.navigate('Schedules', {})}
            >
              <Text style={[styles.statLinkText, { color: themedColors.text }]}>{t('schedules.viewReadings')}</Text>
              <EyeIcon size={16} color={themedColors.text} />
            </TouchableOpacity>
          </View>
        </View>

        {/* Table Card with Filters */}
        <View style={[styles.tableCard, { backgroundColor: themedColors.card, borderColor: themedColors.cardBorder }]}>
          {/* Filter Section */}
          <View style={styles.tableHeader}>
            <View style={[styles.filterTitleRow, { flexDirection: 'row' }]}>
              {/* Left side: print/export icons */}
              <View style={styles.filterIcons}>
                <IconButton
                  variant="secondary"
                  size="sm"
                  icon={<PrinterIcon color={themedColors.textSecondary} />}
                  accessibilityLabel={t('common.print')}
                  onPress={handlePrint}
                />
                <IconButton
                  variant="secondary"
                  size="sm"
                  icon={<FileOutputIcon color={themedColors.textSecondary} />}
                  accessibilityLabel={t('common.export')}
                  onPress={handleExportCSV}
                />
              </View>
              {/* Title */}
              <Text style={[styles.tableTitle, { color: themedColors.text }]}>{t('schedules.readingStatusByOrg')}</Text>
            </View>

            <View style={[styles.filtersRow, { flexDirection: 'row' }]}>
              {/* Apply Filter button */}
              <TouchableOpacity style={[styles.applyFilterButton, { backgroundColor: themedColors.tableHeaderBg, borderWidth: tokens.spacing.absolute["px"], borderColor: themedColors.tableBorder }]} onPress={handleApplyFilter}>
                <Text style={[styles.applyFilterText, { color: themedColors.text }]}>{t('schedules.applyFilter')}</Text>
              </TouchableOpacity>

              {/* Year dropdown */}
              <View style={styles.filterYearGroup}>
                <Dropdown
                  value={selectedYear.toString()}
                  options={Array.from({ length: 10 }, (_, i) => {
                    const y = new Date().getFullYear() - 5 + i;
                    return { value: y.toString(), label: y.toString() };
                  })}
                  placeholder={selectedYear.toString()}
                  onChange={(val) => setSelectedYear(parseInt(val))}
                />
              </View>

              {/* Organization dropdown */}
              <View style={styles.filterGroup}>
                <Dropdown
                  value={pendingOrganization}
                  options={organizationOptions}
                  placeholder={t('schedules.allOrganizations')}
                  onChange={setPendingOrganization}
                />
              </View>
            </View>
          </View>

          {/* Table */}
          <View style={styles.table}>
            {/* Table Header Row - RTL order */}
            <View style={[styles.tableHeaderRow, { borderBottomColor: themedColors.tableBorder }]}>
              <Text style={[styles.tableHeaderCell, styles.colAction, { color: themedColors.textSecondary }]}>{t('schedules.viewSchedule')}</Text>
              <Text style={[styles.tableHeaderCell, styles.colCompletion, { color: themedColors.textSecondary }]}>{t('schedules.completionRate')}</Text>
              <Text style={[styles.tableHeaderCell, styles.colOverdue, { color: themedColors.textSecondary }]}>{t('schedules.overdueCount')}</Text>
              <Text style={[styles.tableHeaderCell, styles.colDue, { color: themedColors.textSecondary }]}>{t('schedules.dueCount')}</Text>
              <Text style={[styles.tableHeaderCell, styles.colReadings, { color: themedColors.textSecondary }]}>{t('schedules.totalReadings')}</Text>
              <Text style={[styles.tableHeaderCell, styles.colKpis, { color: themedColors.textSecondary }]}>{t('schedules.kpiCount')}</Text>
              <Text style={[styles.tableHeaderCell, styles.colOrg, { color: themedColors.textSecondary }]}>{t('schedules.organization')}</Text>
            </View>

            {/* Table Body */}
            {loading && orgSummaries.length === 0 ? (
              <TableSkeleton rows={5} />
            ) : orgSummaries.length === 0 ? (
              <View style={styles.emptyState}>
                <Text style={[styles.emptyStateText, { color: themedColors.textMuted }]}>{t('schedules.noScheduledReadings')}</Text>
              </View>
            ) : (
              orgSummaries.map((org, index) => (
                <View
                  key={org.org_id}
                  style={[
                    styles.tableRow,
                    { backgroundColor: themedColors.tableRowBg, borderBottomColor: themedColors.tableBorder },
                    index === orgSummaries.length - 1 && styles.tableRowLast,
                  ]}
                >
                  {/* View Schedule button */}
                  <View style={[styles.tableCell, styles.colAction]}>
                    <TouchableOpacity
                      style={styles.viewButton}
                      onPress={() => navigation.navigate('Schedules', { organizationId: org.org_id })}
                    >
                      <ChevronLeftIcon size={14} color={themedColors.textSecondary} />
                      <Text style={[styles.viewButtonText, { color: themedColors.textSecondary }]}>{t('schedules.viewSchedule')}</Text>
                    </TouchableOpacity>
                  </View>

                  {/* Completion ring */}
                  <View style={[styles.tableCell, styles.colCompletion, { alignItems: 'center' }]}>
                    <ProgressRing percentage={org.completion_percentage} size={56} strokeWidth={5} />
                  </View>

                  {/* Overdue count */}
                  <Text style={[styles.tableCellText, styles.colOverdue, { color: org.overdue_count > 0 ? tokens.colors.raw.red["500"] : themedColors.text, fontFamily: FONTS.numbers }]}>
                    {org.overdue_count}
                  </Text>

                  {/* Due count */}
                  <Text style={[styles.tableCellText, styles.colDue, { color: themedColors.text, fontFamily: FONTS.numbers }]}>
                    {org.due_count}
                  </Text>

                  {/* Total readings */}
                  <Text style={[styles.tableCellText, styles.colReadings, { color: themedColors.text, fontFamily: FONTS.numbers }]}>
                    {org.total_readings}
                  </Text>

                  {/* KPI count */}
                  <Text style={[styles.tableCellText, styles.colKpis, { color: themedColors.text, fontFamily: FONTS.numbers }]}>
                    {org.kpi_count}
                  </Text>

                  {/* Organization name */}
                  <Text style={[styles.tableCellText, styles.colOrg, { color: themedColors.text }]} numberOfLines={2}>
                    {translateOrg(org.org_name)}
                  </Text>
                </View>
              ))
            )}
          </View>

          {/* Pagination + Showing info row */}
          {totalPages > 0 && (
            <View style={[styles.paginationRow, { borderTopColor: themedColors.tableBorder, flexDirection: rtlRow }]}>
              {/* Showing X-Y of Z - right side */}
              {totalCount > 0 && (
                <Text style={[styles.paginationInfoText, { color: themedColors.textMuted }]}>
                  {paginationText}
                </Text>
              )}

              {/* Spacer */}
              <View style={{ flex: 1 }} />

              {/* Pagination controls - left side */}
              <View style={[styles.paginationControls, { flexDirection: rtlRow }]}>
                {/* Previous button */}
                <TouchableOpacity
                  style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
                  onPress={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <Text style={[styles.paginationButtonText, { color: themedColors.textMuted }]}>{t('common.previous')}</Text>
                  <ChevronRightIcon size={16} color={currentPage === 1 ? themedColors.cardBorder : themedColors.textMuted} />
                </TouchableOpacity>

                {/* Page numbers */}
                <View style={[styles.paginationNumbers, { flexDirection: rtlRow }]}>
                  {getPageNumbers().map((page) => (
                    <TouchableOpacity
                      key={page}
                      style={[
                        styles.paginationNumber,
                        { backgroundColor: themedColors.card, borderColor: themedColors.cardBorder },
                        currentPage === page && styles.paginationNumberActive,
                      ]}
                      onPress={() => setCurrentPage(page)}
                    >
                      <Text style={[
                        styles.paginationNumberText,
                        { color: themedColors.textMuted },
                        currentPage === page && [styles.paginationNumberTextActive, { color: themedColors.text }],
                      ]}>{page}</Text>
                    </TouchableOpacity>
                  ))}
                </View>

                {/* Next button */}
                <TouchableOpacity
                  style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
                  onPress={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronLeftIcon size={16} color={currentPage === totalPages ? themedColors.cardBorder : themedColors.textMuted} />
                  <Text style={[styles.paginationButtonText, { color: themedColors.textMuted }]}>{t('common.next')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </ScrollView>
    </PageContainer>
  );
}

// ============================================================
// Styles
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    flex: 1,
    padding: tokens.spacing.absolute["4"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacing.absolute["5"],
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  pageTitle: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.medium,
    textAlign: getTextAlign(isRTL),
  },
  generateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.green["500"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderRadius: tokens.radii.absolute["radius-8"],
    gap: tokens.spacing.absolute["2"],
  },
  generateButtonDisabled: {
    opacity: 0.6,
  },
  generateButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: COLORS.white,
  },

  // Stats Cards
  statsContainer: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  statsContainerMobile: {
    flexDirection: 'column',
  },
  statCard: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    minHeight: 128,
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  statLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
    width: '100%',
  },
  statValue: {
    fontSize: 30,
    fontWeight: '700',
    textAlign: 'center',
  },
  statSubtitle: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    textAlign: 'center',
  },
  statLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  statLinkText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
  },

  // Table Card
  tableCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    overflow: 'hidden',
    marginBottom: tokens.spacing.absolute["6"],
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  tableHeader: {
    padding: tokens.spacing.absolute["5"],
    gap: tokens.spacing.absolute["5"],
  },
  filterTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  filterIcons: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["2"],
  },
  tableTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    textAlign: getTextAlign(isRTL),
    flex: 1,
  },
  filtersRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  filterGroup: {
    flex: 1,
    maxWidth: 400,
  },
  filterYearGroup: {
    width: 120,
  },
  applyFilterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 5,
    borderRadius: tokens.radii.absolute["radius-8"],
    minHeight: 32,
  },
  applyFilterText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
  },

  // Table
  table: {
    overflow: 'hidden',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    paddingVertical: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
  },
  tableHeaderCell: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.bold,
    textAlign: 'center',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: tokens.spacing.absolute["2_5"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    alignItems: 'center',
  },
  tableRowLast: {
    borderBottomWidth: 0,
  },
  tableCell: {
    justifyContent: 'center',
  },
  tableCellText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: 'center',
  },

  // Column widths (RTL: action on left, org on right)
  colAction: {
    width: 132,
  },
  colCompletion: {
    width: 100,
  },
  colOverdue: {
    width: 80,
  },
  colDue: {
    width: 80,
  },
  colReadings: {
    width: 130,
  },
  colKpis: {
    width: 100,
  },
  colOrg: {
    flex: 1,
    textAlign: getTextAlign(isRTL),
  },

  viewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["2_5"],
    paddingVertical: tokens.spacing.absolute["1_5"],
  },
  viewButtonText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
  },
  emptyState: {
    padding: tokens.spacing.absolute["10"],
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
  },

  // Pagination
  paginationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderTopWidth: tokens.spacing.absolute["px"],
  },
  paginationControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  paginationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    gap: tokens.spacing.absolute["1"],
  },
  paginationButtonDisabled: {
    opacity: 0.4,
  },
  paginationButtonText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
  },
  paginationNumbers: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["1"],
  },
  paginationNumber: {
    width: 32,
    height: 32,
    borderRadius: tokens.radii.absolute["radius-6"],
    borderWidth: tokens.spacing.absolute["px"],
    justifyContent: 'center',
    alignItems: 'center',
  },
  paginationNumberActive: {
    borderColor: tokens.colors.raw.slate["700"],
  },
  paginationNumberText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.numbers,
  },
  paginationNumberTextActive: {
    fontFamily: FONTS.medium,
  },
  paginationInfoText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
  },

  // Dropdown
  dropdownContainer: {
    position: 'relative',
  },
  dropdownButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 5,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    minHeight: 32,
  },
  dropdownButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dropdownMenu: {
    width: '80%',
    maxHeight: 300,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    overflow: 'hidden',
  },
  dropdownMenuScroll: {
    maxHeight: 300,
  },
  dropdownItem: {
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderBottomWidth: tokens.spacing.absolute["px"],
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
  },
});

const styles = getStyles(true);
